<template>
    <div class="person-role-creator flex flex-col h-full">
      <h2 class="text-2xl font-semibold text-gray-900 mb-6">Create Role</h2>
      
      <form @submit.prevent="submitForm" class="space-y-4">
        <!-- Person Selection -->
        <div class="form-group" v-if="!personId">
          <label for="person" class="block text-sm font-medium text-gray-700">Person</label>
          <div class="mt-1">
            <div class="relative">
              <input
                type="text"
                v-model="searchQuery"
                @input="searchPeople"
                @focus="showDropdown = true"
                @blur="handleBlur"
                class="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search for a person..."
              />
              <!-- Dropdown -->
              <div v-if="showDropdown && people.length > 0" class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                <div
                  v-for="person in people"
                  :key="person.person_id"
                  @mousedown="selectPerson(person)"
                  class="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
                >
                  <div class="flex items-center">
                    <span class="font-medium text-gray-900">{{ person.person_name }}</span>
                  </div>
                </div>
  
                <!-- Infinite loading dropdown -->
                <InfiniteLoading :identifier="infiniteKey" @infinite="searchPeople"/>
              </div>
  
              <!-- Selected Person Display -->
              <div v-if="selectedPerson" class="mt-2 p-2 bg-gray-50 rounded-md">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="font-medium text-gray-900">{{ selectedPerson.person_name }}</span>
                  </div>
                  <button
                    type="button"
                    @click="clearSelection"
                    class="text-gray-400 hover:text-gray-500"
                  >
                    <span class="sr-only">Remove</span>
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Role Selection -->
        <div class="form-group">
          <div v-if="availableRoleTypes.length > 1" class="mt-1">
            <label for="roleType" class="block text-sm font-medium text-gray-700">Role For</label>
            <select v-model="formData.relatable_type" 
              class="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
              required
              @change="fetchRelatableOptions"
            >
              <option v-for="roleType in availableRoleTypes" :key="roleType" :value="roleType">
                {{ formatName(roleType) }}
              </option>
            </select>
          </div>
          <div class="mt-1">
            <label for="role" class="block text-sm font-medium text-gray-700">Role</label>
            <select
              v-model="formData.role" 
              class="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            >
              <option v-for="role in availableRoles" :key="role" :value="role">
                {{ formatName(role) }}
              </option>
            </select>
          </div>
        </div>
  
        <!-- State Selection -->
        <div class="form-group">
          <label for="state" class="block text-sm font-medium text-gray-700">State</label>
          <div class="mt-1">
            <select
              v-model="formData.state"
              class="form-select block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            >
              <option v-for="state in validStates" :key="state" :value="state">
                {{ state.charAt(0).toUpperCase() + state.slice(1) }}
              </option>
            </select>
          </div>
        </div>
  
        <div class="form-group" v-if="!props.relatableId">
          <RelatableSelect
            v-model="formData.relatable_id"
            name="relatable"
            :label="formatName(formData.relatable_type)"
            :items="relatableOptions "
          />
        </div>
        <div class="form-group" v-else>
          <label for="relatable_name" class="block text-sm font-medium text-gray-700">{{ formatName(props.relatableType) }}</label>
          <div class="mt-1">
            {{ props.relatableName }}
          </div>
        </div>
  
        <!-- Effective Dates -->
        <div class="grid grid-cols-2 gap-4">
          <div class="form-group">
            <label for="effective_at" class="block text-sm font-medium text-gray-700">Effective Date</label>
            <div class="mt-1">
              <input
                type="date"
                v-model="formData.effective_at"
                class="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
          <div class="form-group">
            <label for="ineffective_at" class="block text-sm font-medium text-gray-700">Ineffective Date</label>
            <div class="mt-1">
              <input
                type="date"
                v-model="formData.ineffective_at"
                class="form-input block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
  
        <!-- Submit Button -->
        <div class="flex justify-end">
          <Tippy :content="tooltipConfig.content" :enabled="tooltipConfig.enabled" :trigger="tooltipConfig.trigger">
            <button
              type="submit"
              class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSubmitting || !hasPersonSelected"
            >
              {{ isSubmitting ? 'Creating...' : 'Create Role' }}
            </button>
          </Tippy>
        </div>
      </form>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { Tippy } from 'vue-tippy'
  import 'tippy.js/dist/tippy.css'
  import axios from 'axios'
  import InfiniteLoading from "v3-infinite-loading"
  import "v3-infinite-loading/lib/style.css"
  import FlashMessages from '@services/FlashMessages'
  import RelatableSelect from '@components/RelatableSelect.vue'
  
  const props = defineProps({
    personId: {
      type: [Number, String],
      default: null
    },
    relatableType: {
      type: String,
      default: ''
    },
    relatableId: {
      type: [Number, String],
      default: null
    },
    roles: {
      type: [Object, Array],
      default: () => {}
    },
    relatableName: {
      type: String,
      default: ''
    }
  })
  
  const emit = defineEmits(['created', 'error'])
  
  const formData = ref({
    person_id: props.personId,
    role: props.role,
    state: 'active',
    relatable_id: props.relatableId,
    relatable_type: props.relatableType,
    effective_at: null,
    ineffective_at: null
  })
  
  const searchQuery = ref('')
  const people = ref([])
  const selectedPerson = ref(null)
  const selectedRole = ref(null)
  const showDropdown = ref(false)
  const isSubmitting = ref(false)
  const page = ref(1)
  const infiniteKey = ref(0)
  
  const validStates = [
    'active',
    'inactive',
    'dropped',
    'graduated',
    'under_review',
    'suspended'
  ]
  
  const formatName = (name) => {
    if (name) {
      return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }
  }
  
  const relatableOptions = ref([])
  
  onMounted(() => {
    console.log('props!', props)
  
    if (props.relatableType && !props.relatableId) {
      fetchRelatableOptions()
    }
    if (availableRoles.value.length === 1 && !props.personId) {
        formData.value.role = availableRoles.value[0]
        console.log("formData.value.role", formData.value.role)
    }
    console.log("*** onMounted ***")
    console.log('availableRoles', availableRoles.value)
    console.log('relatableType', props.relatableType)
    console.log('props.roles', props.roles)
    console.log('formData', formData.value)
    console.log("*** End of onMounted ***")
  })
  
  onUnmounted(() => {
  })
  
  const availableRoles = computed(() => {
    let selectedRoleType = formData.value.relatable_type
    console.log("*** availableRoles ***")
    console.log("props.Roles", props.roles)
    console.log("selectedRoleType", selectedRoleType)
    console.log("availableRoles for selectedRoleType", props.roles[selectedRoleType])
    let returnValue = []
    if (Array.isArray(props.roles)) {
      returnValue = props.roles 
    } else if (selectedRoleType && props.roles[selectedRoleType] && props.roles[selectedRoleType].length > 0) {
      console.log("selectedRoleType", selectedRoleType)
      returnValue = props.roles[selectedRoleType]
    } else {
      returnValue = []
    }
    console.log("returnValue", returnValue)
    console.log("*** End of availableRoles ***")
    return returnValue
  })

  const availableRoleTypes = computed(() => {
    if (Array.isArray(props.roles)) {
      return []
    } else {
      return Object.keys(props.roles).filter(roleType => props.roles[roleType].length > 0)
    } 
  })

  const updateRelatableType = (opt) => {
    console.log("updateRelatableType", opt)
    if (opt && opt.roleType && opt.roleType !== props.relatableType) {
      formData.value.relatable_type = opt.roleType
      formData.value.relatable_id = null
      fetchRelatableOptions()
    }
  }
  
  const searchPeople = async ($state) => {
    if (!searchQuery.value) {
      people.value = []
      return
    }
  
    let payload = {
      page: page.value,
      country_id: props.countryId,
      // training_center_group_id: props.trainingCenterGroupId,
      office_id: props.officeId,
      search: searchQuery.value,
    }
  
    console.log("search query", searchQuery.value)
    console.log("payload", payload)
  
    try {
      const response = await axios.get('/people/advanced_lookup.json', {
        params: payload
      })
  
      console.log("response.data", response.data)
      
      if (response.data.records.length === 0) {
        $state.complete()
      } else {
        people.value.push(...response.data.records)
        page.value += 1
        $state.loaded()
      }
    } catch (error) {
      console.error('Error searching people:', error)
    }
  }
  
  const selectPerson = (person) => {
    selectedPerson.value = person
    formData.value.person_id = person.person_id
    searchQuery.value = ''
    showDropdown.value = false
  }
  
  const clearSelection = () => {
    selectedPerson.value = null
    formData.value.person_id = null
    searchQuery.value = ''
  }
  
  const handleBlur = () => {
    // Delay hiding the dropdown to allow for click events
    setTimeout(() => {
      showDropdown.value = false
    }, 200)
  }
  const hasPersonSelected = computed(() => {
    return formData.value.person_id !== null
  })
  
  // Tooltip configuration for vue-tippy
  const tooltipConfig = computed(() => {
    const shouldShowTooltip = !hasPersonSelected.value && !isSubmitting.value
    return {
      content: 'Please select a person before creating a role',
      enabled: shouldShowTooltip,
      trigger: 'mouseenter focus'
    }
  })
  
  const submitForm = async () => {
    // Prevent submission if no person is selected
    if (!hasPersonSelected.value) {
      FlashMessages.error('Please select a person before creating a role.')
      return
    }

    isSubmitting.value = true
    console.log("submitting....")
    console.log("formData...", formData.value)
    //return
    try {
  
      
     const response = await axios.post('/person_roles', {
        person_role: {
          person_id: formData.value.person_id,
          role: formData.value.role,
          state: formData.value.state,
          relatable_id: formData.value.relatable_id,
          relatable_type: formData.value.relatable_type,
          effective_at: formData.value.effective_at,
          ineffective_at: formData.value.ineffective_at,
          person_attributes: {
            id: formData.value.person_id
          }
        }
      }, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
      
      // const serializedResponse = new SerializedResponse(PersonRole, response.data)
      // console.log("serializedResponse", serializedResponse)
      // console.log("serializedResponse.modelData", serializedResponse.modelData)
  
      console.log("response", response)
      console.log("response.data", response.data)
  
  
      // emit('created', serializedResponse.modelData)
      emit('created', response.data)
      FlashMessages.success('Person role created successfully!')
      window.location.reload()
      resetForm()
    } catch (error) {
      console.error('Error creating person role:', error)
      emit('error', error)
      FlashMessages.error('Failed to create person role.')
    } finally {
      isSubmitting.value = false
    }
}
  
  const resetForm = () => {
    formData.value = {
      person_id: props.personId,
      role: null,
      state: 'active',
      relatable_id: null,
      relatable_type: null,
      effective_at: null,
      ineffective_at: null
    }
  }
  
  // Watch for search query changes to reset pagination
  watch(searchQuery, () => {
    page.value = 1
    people.value = []
    infiniteKey.value += 1
  })

  const fetchRelatableOptions = async () => {
    console.log("fetching relatable options for", formData.value.relatable_type)
    
    if (!formData.value.relatable_type) {
      console.log("No relatable type specified")
      return
    }

    try {
      const params = {
        relatable_type: formData.value.relatable_type,
        role: formData.value.role
      }

      // Add person_id if available
      if (formData.value.person_id) {
        params.person_id = formData.value.person_id
      }

      // Add country_id from props if available (for backward compatibility)
      if (props.countryId) {
        params.country_id = props.countryId
      }

      const response = await axios.get('/person_roles/get_relatable_options', {
        params: params
      })
      
      console.log("fetched relatable options: ", response.data)
      relatableOptions.value = response.data
    } catch (error) {
      console.error('Error fetching relatable options:', error)
      relatableOptions.value = []
    }
  }
  
  </script>
  
  <style scoped>
  .person-role-creator {
    @apply p-8 bg-white rounded-lg shadow;
  }
  
  .form-group {
    @apply mb-4;
  }
  </style>
